import type { ServiceRequestStatus } from '@prisma/client';
import type { UIMessage, UIMessageChunk } from 'ai';

export enum CommendaAiFeedbackRating {
  UP = 'UP',
  DOWN = 'DOWN',
}

export enum CommendaAiFeedbackCategory {
  GOOD_RESPONSE = 'GOOD_RESPONSE',
  SAVED_ME_TIME = 'SAVED_ME_TIME',
  CAUGHT_SOMETHING_I_MISSED = 'CAUGHT_SOMETHING_I_MISSED',
  WELL_PRESENTED = 'WELL_PRESENTED',
  WRONG_INFORMATION = 'WRONG_INFORMATION',
  WRONG_ENTITY_OR_COMPANY = 'WRONG_ENTITY_OR_COMPANY',
  MISSING_CAPABILITY = 'MISSING_CAPABILITY',
  INCORRECT_REFUSAL = 'INCORRECT_REFUSAL',
  ACTED_WITHOUT_CONFIRMING = 'ACTED_WITHOUT_CONFIRMING',
  INCOMPLETE_ANSWER = 'INCOMPLETE_ANSWER',
  FORMATTING_OR_PRESENTATION = 'FORMATTING_OR_PRESENTATION',
  SOMETHING_ELSE = 'SOMETHING_ELSE',
}

/** The verdict a reader already saved on a message, minus the ids it is keyed by. The feedback
 * modal prefills from this so reopening a thumb shows what was submitted. */
export interface CommendaAiSavedMessageFeedback {
  rating: CommendaAiFeedbackRating;
  category: CommendaAiFeedbackCategory | null;
  comment: string | null;
}

export interface CommendaAiMessageFeedbackSummary
  extends CommendaAiSavedMessageFeedback {
  messageId: string;
}

export interface CommendaAiMessageFeedbackParams {
  conversationId: string;
  messageId: string;
  rating: CommendaAiFeedbackRating;
  category?: CommendaAiFeedbackCategory;
  comment?: string;
}

export interface CommendaAiMessageFeedbackResponse {
  feedbackId: string;
}

/** What every chat mutation resolves to; it never throws. `status` is set when the backend refused the call. */
export interface CommendaAiMutationResult<T = undefined> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

export interface CommendaAiMessageFeedbackResult {
  success: boolean;
  data?: CommendaAiMessageFeedbackResponse;
  error?: string;
}

/** Optional detail submitted from the feedback modal on top of an already-saved rating. */
export interface CommendaAiMessageFeedbackDetails {
  category?: CommendaAiFeedbackCategory;
  comment?: string;
}

/** One repeated element harvested from the page: a table row, a card in a list.
 * The id is the record id the row rendered; href is the row's link when the id lives only there. */
export interface CommendaAiPageItem {
  type: string;
  id?: string;
  href?: string;
  text: string;
}

/** A named section of the page, from a data-ai-section annotation. `truncatedCount` is how many
 * items were cut to fit the cap. */
export interface CommendaAiPageSection {
  name: string;
  text?: string;
  items?: CommendaAiPageItem[];
  truncatedCount?: number;
}

/** What the panel harvested from the page at send time. Wire shape of the backend's
 * CommendaAiPageContextDto — every string and array is capped there, so the harvester
 * applies the same caps before sending. */
export interface CommendaAiPageContext {
  screen: string;
  label?: string;
  ids?: Record<string, string>;
  sections?: CommendaAiPageSection[];
  /** The page's rendered text, sent only when it has no annotated sections. */
  visibleText?: string;
  isLoading?: boolean;
}

/** PRIVATE: only the owner can read and reply. WORKSPACE: everyone in the workspace can. */
export type CommendaAiConversationVisibility = 'PRIVATE' | 'WORKSPACE';

/** What the header shows about who can access a chat, resolved for the current viewer. */
export interface CommendaAiConversationAccessView {
  visibility: CommendaAiConversationVisibility;
  /** The owner's name, shown to everyone but the owner so they know who shared it. */
  sharedByName?: string | null;
  /** Set when the viewer may not change access; the pill then renders read-only with this as its tooltip. */
  readOnlyReason?: string;
}

/** Per-message facts the backend stores but the AI SDK's UIMessage has no field for. Rides on
 * `metadata`, which is absent on a message just sent in this session. */
export interface CommendaAiMessageMetadata {
  createdAt?: string;
  senderName?: string;
  /** The stored question-and-answer records on this message, from the backend's timelineBlocks.
   * Present only on a replayed message: a card answered in this session renders optimistically,
   * because the chat loads history once at mount and never refetches it. */
  timelineBlocks?: unknown[] | null;
}

/** Where a conversation's document uploads stand. The chat polls while it is not idle, and a move to
 * idle is when the backend has written the "documents processed" message. */
export enum CommendaAiDocumentProcessingStage {
  AWAITING_UPLOAD = 'awaiting_upload',
  PROCESSING = 'processing',
  IDLE = 'idle',
}

export enum CommendaAiReferenceType {
  ENTITY = 'entity',
  KEY_PERSON = 'keyPerson',
  SERVICE = 'service',
}

export interface CommendaAiReferenceRow {
  label: string;
  value: string;
  overflow?: string;
}

export interface CommendaAiReferencePreview {
  type: CommendaAiReferenceType;
  title: string;
  country?: string;
  status?: ServiceRequestStatus;
  isOnHold?: boolean;
  rows: CommendaAiReferenceRow[];
}

/** Firm scope: a conversation with no company, spanning an affiliate member's clients. Sent so the
 * backend can tell it from "no filter", which a null company means on its own. */
export type AgentConversationScope = 'firm';

/** RUNNING and AWAITING_HUMAN are the two states a poller must keep watching; the rest are terminal. */
export type AgentRunLiveState =
  | 'RUNNING'
  | 'AWAITING_HUMAN'
  | 'COMPLETED'
  | 'FAILED'
  | 'NO_RUN';

/**
 * One conversation record as the unified API returns it.
 *
 * The first block is the API's own key allowlist. The rest are the access and read-state fields
 * chat renders; they are optional because a profile that does not track them leaves them out, and
 * a missing one reads as the safe default rather than failing the row.
 */
export interface AgentConversationSummary {
  conversationId: string;
  /** The profile the conversation is bound to; null once that profile has been deleted. */
  profile: string | null;
  title: string | null;
  status: string;
  companyId: number | null;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string | null;
  userId?: string;
  visibility?: CommendaAiConversationVisibility;
  createdByName?: string | null;
  lastReadAt?: string | null;
  scheduledAgentId?: string | null;
  agentType?: string;
}

export interface AgentConversationEnvelopeMessage {
  messageId: string;
  role: 'USER' | 'ASSISTANT';
  content: string;
  status: string;
  createdAt: string;
  parts: UIMessage['parts'];
  /** QUESTION blocks only. The card finds itself here by call id; everything else is already a part. */
  timelineBlocks: unknown[];
  attachments: unknown[];
  errorMessage: string | null;
  modelId: string | null;
  liveState: AgentRunLiveState;
  /** Who sent the turn, on a conversation shared with a workspace. Null on an assistant turn, and
   * absent on a read that carries no sender join. */
  sender?: { id: string; name: string | null } | null;
}

export interface AgentConversationEnvelope {
  conversation: AgentConversationSummary & {
    id: string;
    [key: string]: unknown;
  };
  liveState: AgentRunLiveState;
  pendingGate: {
    kind: string;
    stepName: string;
    sequenceNumber: number;
  } | null;
  messages: AgentConversationEnvelopeMessage[];
}

export interface AgentConversationListEnvelope {
  conversations: (AgentConversationSummary & {
    liveState: AgentRunLiveState;
  })[];
  count: number;
}

export interface AgentConversationCreatedView {
  conversation: AgentConversationSummary;
  /** Present only when the create body carried a first message. */
  accepted: { conversationId: string; messageId: string | null } | null;
}

export interface AgentConversationSendAcceptedView {
  conversationId: string;
  messageId: string | null;
}

export interface AgentConversationRenamedView {
  conversation: AgentConversationSummary;
}

/** One saved verdict. Run-scoped, so `messageId` is null on a run that answered with no message. */
export interface AgentConversationFeedbackVerdict {
  runId: string;
  messageId: string | null;
  rating: CommendaAiFeedbackRating;
  category: CommendaAiFeedbackCategory | null;
  comment: string | null;
}

export interface AgentConversationFeedbackListEnvelope {
  feedback: AgentConversationFeedbackVerdict[];
  count: number;
}

/** The question card a turn answers, by the tool call that asked it. */
export interface AgentConversationAnswerTo {
  toolCallId: string;
  answers: { questionIndex: number; chosen: string[] }[];
}

/** Side effects the read records. Both come off an email link to a scheduled agent's run session,
 * which is the only place the reader's entry point is knowable. */
export interface AgentConversationReadOptions {
  runNumber?: number;
  openedFrom?: 'email' | 'app';
}

export interface CreateAgentConversationBody {
  /** The profiles the conversation may run as. Chat sends exactly one. */
  profile: string[];
  message?: string;
  companyId?: number;
  scope?: AgentConversationScope;
  /** The browser's IANA zone for the first message, as on a send. Never saved. */
  timeZone?: string;
}

export interface ListAgentConversationsQuery {
  profile?: string;
  companyId?: number | null;
  scope?: AgentConversationScope;
  /** A scheduled agent's run sessions are that agent's own history, not a thread in this list. */
  excludeRunSessions?: boolean;
  /** The workflow agent's history for one workflow. By template rather than version: editing a live
   * workflow forks a new version, which would otherwise read as having no history. */
  workflowTemplateId?: string;
  limit?: number;
}

/** What the unified send accepts. Every field beyond `message` is refused for profiles that do not
 * declare it, so a surface sends only its own. */
export interface SendAgentConversationMessageBody {
  message: string;
  mode?: string;
  modelId?: string;
  attachments?: {
    s3Key?: string;
    url: string;
    fileName: string;
    mimeType: string;
    sizeBytes?: number;
  }[];
  builderContext?: unknown;
  targetWorkflowTemplateVersionId?: string;
  pageContext?: unknown;
  skillKeys?: string[];
  /** Chat-only: the turn answers a question card rather than starting a new thought. The backend
   * stores its own marker on the user message from this, which is what hides the bubble on reload. */
  answerTo?: AgentConversationAnswerTo;
  /** Chat-only: rerun the stored last user message in place of its reply. `message` is still required
   * by validation but ignored, and no second user message is stored. */
  regenerate?: boolean;
  /** Every agent: the browser's IANA zone right now, so dates in the reply are the user's local ones.
   * Sent on every turn and never saved, so it follows the user when they travel. */
  timeZone?: string;
  /** Chat-only: a finished data-room upload this message refers to. Its files are never attachments. */
  documentUploadId?: string;
}

export type AgentQueryValue = string | number | boolean | null | undefined;

export interface AgentAttachmentUpload {
  signedUrl: string;
  hostedUrl: string;
  s3Key: string;
}

export interface AgentProfileModel {
  modelId: string;
  label: string;
  provider: string;
}

export interface AgentProfileModelListEnvelope {
  profileKey: string;
  models: AgentProfileModel[];
  count: number;
}

export type AgentConversationMessagePart = UIMessage['parts'][number];

export type AgentConversationDataPart = {
  type: `data-${string}`;
  id?: string;
  data: unknown;
};

export type AgentConversationTextPart = Extract<
  AgentConversationMessagePart,
  { type: 'text' | 'reasoning' }
>;

/** Which rows are not this turn's reply, and on a resume the reply the history already drew. */
export interface AgentReplyBaseline {
  priorMessageIds: ReadonlySet<string>;
  drawn: AgentConversationEnvelopeMessage | null;
}

/** What the last message of a turn carries to the send. */
export interface AgentTurnContent {
  text: string;
  attachments: NonNullable<SendAgentConversationMessageBody['attachments']>;
}

export type AgentReplyChunkController =
  ReadableStreamDefaultController<UIMessageChunk>;

export interface QueuedConversationTransportOptions {
  /** Extra fields every send carries, e.g. the builder's target version and model. Merged under
   * the per-call `body`, so a call-site field wins. */
  buildSendBody?: () => Partial<SendAgentConversationMessageBody>;
  pollIntervalMs?: number;
  /** Fired when a send or a poll returns 401, so an expired session drives the app's sign-out. */
  onUnauthorized?: () => void;
  /** Fired once the server has accepted a turn, before its reply is polled. */
  onTurnSent?: () => void;
}

export interface CommendaAiUploadFile {
  file: File;
  path: string;
}

export enum CommendaAiFileSource {
  GENERATED = 'generated',
}

export interface CommendaAiGeneratedFileSuggestion {
  entityId?: number;
  documentSubType?: string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  folderId?: string;
}

export interface CommendaAiSavedFile {
  fileId: number;
  entityId: number;
  folderId: string;
  filePlacementId: string | null;
  savedByUserId: string;
  savedAt: string;
}

export interface CommendaAiGeneratedFileMetadata {
  source: CommendaAiFileSource;
  suggestion?: CommendaAiGeneratedFileSuggestion;
  saved?: CommendaAiSavedFile;
}

export interface CommendaAiMessageFileRef {
  conversationId: string;
  messageId: string;
}

export interface CommendaAiGeneratedFileRef extends CommendaAiMessageFileRef {
  fileUrl: string;
}

export interface CommendaAiDocumentTypeOption {
  documentSubType: string;
  documentName: string;
  isTimeSensitive: boolean;
  isUnique: boolean;
}

export interface CommendaAiGeneratedFileSaveOptions {
  documentTypes: CommendaAiDocumentTypeOption[];
  documentType: CommendaAiDocumentTypeOption | null;
  folder: { id: string; path: string } | null;
  replacesFileName: string | null;
}

export interface SaveCommendaAiGeneratedFileBody
  extends CommendaAiGeneratedFileRef {
  entityId: number;
  folderId: string;
  documentSubType?: string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  replaceExisting?: boolean;
}
